import { create } from "zustand";
// the protocol module alone, not the package: this file is on every page's first load, and the
// package's index brings the whole sim and every zone's content with it (GB-214)
import { PROTOCOL_VERSION } from "@charter/game/protocol";
import { BUILD } from "@/config/build";
import { IS_DEV } from "@/config/env";
import { isNative } from "@/platform/platform";
import {
  decideBuild,
  MAX_RELOADS,
  noteReload,
  reloadsFor,
  sessionStore,
  stashCarryOver,
  type CarryOver,
} from "@/platform/followBuild";
import { forceUpdate } from "@/platform/pwaUpdate";
import { track } from "./telemetry";

/** What BuildNotice shows when the client can't (or mustn't) reload into the server's build. */
export type BuildNotice = "none" | "stuck" | "update" | "banner";

export const useBuildNotice = create<{ notice: BuildNotice; set: (n: BuildNotice) => void }>(
  (set) => ({ notice: "none", set: (notice) => set({ notice }) }),
);

/** Set once this page has started a forced reload: a later trigger on the way out is not another. */
let reloading = false;

/**
 * Follows the server's build (platform/followBuild.ts): reloads into it, or raises a notice.
 * Called with what the ws welcome and /v1/config name. Returns true when this client must not go
 * on with the server it just heard from (it is reloading, stuck, or a native build on another
 * protocol); an admin page only gets a banner. `carry`: what to keep across the reload (the game's
 * unsent chat line and open panels).
 */
export function followServerBuild(
  theirs: string | undefined,
  theirProtocol: { v?: number; min?: number },
  carry?: () => CarryOver,
): boolean {
  // a local Vite client is never the server's bundle, wherever its API points (a dev server)
  if (IS_DEV) return false;
  if (reloading) return true;
  const store = sessionStore();
  const now = Date.now();
  const decision = decideBuild({
    mine: BUILD.commit,
    ...(theirs ? { theirs } : {}),
    myProtocol: PROTOCOL_VERSION,
    ...(theirProtocol.v !== undefined ? { theirProtocol: theirProtocol.v } : {}),
    ...(theirProtocol.min !== undefined ? { theirMinProtocol: theirProtocol.min } : {}),
    native: isNative,
    admin: window.location.pathname.startsWith("/admin"),
    // no storage, no loop guard: no automatic reload at all, the wall's button instead
    reloads: !store ? MAX_RELOADS : theirs ? reloadsFor(store, theirs, now) : 0,
  });
  if (decision === "same") return false;
  if (decision === "reload" && theirs && store) {
    reloading = true;
    if (carry) stashCarryOver(store, carry(), now);
    noteReload(store, theirs, now);
    track("build.reload", { from: BUILD.commit, to: theirs });
    void forceUpdate();
    return true;
  }
  const notice = decision === "reload" ? "stuck" : decision;
  if (useBuildNotice.getState().notice !== notice) {
    useBuildNotice.getState().set(notice);
    track("build.notice", { kind: notice, from: BUILD.commit, to: theirs ?? "" });
  }
  return notice !== "banner";
}
